import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Calendar, Users, User, Building2, FileText, ChevronDown, ChevronUp, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AllTimesheets = ({ user }) => {
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Generation dialog state
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    start_date: '',
    end_date: '',
    scope: 'company', // 'company', 'department', 'individual'
    department: '',
    employee_id: ''
  });

  // Filter state
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    department: '',
    employee_id: ''
  });

  // Expanded rows for details
  const [expandedRows, setExpandedRows] = useState({});

  // Delete confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [timesheetToDelete, setTimesheetToDelete] = useState(null);

  // Bulk delete state
  const [selectedTimesheets, setSelectedTimesheets] = useState([]);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);

  useEffect(() => {
    fetchEmployees();
    fetchTimesheets();
  }, []);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      setEmployees(data.filter(emp => emp.active !== false));
      
      // Extract unique departments
      const uniqueDepts = [...new Set(data.map(emp => emp.department).filter(Boolean))];
      setDepartments(uniqueDepts.sort());
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchTimesheets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.department) params.append('department', filters.department);
      if (filters.employee_id) params.append('employee_id', filters.employee_id);
      
      const res = await fetch(`${BACKEND_URL}/api/timesheets/generated?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch timesheets');
      
      const data = await res.json();
      setTimesheets(data || []);
    } catch (error) {
      console.error('Error fetching timesheets:', error);
      toast.error('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteTimesheet = async () => {
    if (!timesheetToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/timesheets/generated/${timesheetToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to delete timesheet');

      toast.success('Timesheet deleted successfully');
      setShowDeleteDialog(false);
      setTimesheetToDelete(null);
      
      // Refresh the list
      fetchTimesheets();
    } catch (error) {
      console.error('Error deleting timesheet:', error);
      toast.error('Failed to delete timesheet');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTimesheets.length === 0) return;

    try {
      const token = localStorage.getItem('token');
      
      // Delete all selected timesheets
      const deletePromises = selectedTimesheets.map(id =>
        fetch(`${BACKEND_URL}/api/timesheets/generated/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        })
      );

      await Promise.all(deletePromises);

      toast.success(`${selectedTimesheets.length} timesheet${selectedTimesheets.length !== 1 ? 's' : ''} deleted successfully`);
      setShowBulkDeleteDialog(false);
      setSelectedTimesheets([]);
      
      // Refresh the list
      fetchTimesheets();
    } catch (error) {
      console.error('Error deleting timesheets:', error);
      toast.error('Failed to delete timesheets');
    }
  };

  const toggleSelectTimesheet = (timesheetId) => {
    setSelectedTimesheets(prev => {
      if (prev.includes(timesheetId)) {
        return prev.filter(id => id !== timesheetId);
      } else {
        return [...prev, timesheetId];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedTimesheets.length === timesheets.length) {
      setSelectedTimesheets([]);
    } else {
      setSelectedTimesheets(timesheets.map(ts => ts.id));
    }
  };


  const handleGenerateTimesheets = async () => {
    if (!generateForm.start_date || !generateForm.end_date) {
      toast.error('Please select start and end dates');
      return;
    }

    if (generateForm.scope === 'department' && !generateForm.department) {
      toast.error('Please select a department');
      return;
    }

    if (generateForm.scope === 'individual' && !generateForm.employee_id) {
      toast.error('Please select an employee');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/timesheets/generate-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(generateForm)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to generate timesheets');
      }

      const data = await res.json();
      toast.success(`Generated ${data.count} timesheet${data.count !== 1 ? 's' : ''} successfully`);
      
      setShowGenerateDialog(false);
      setGenerateForm({
        start_date: '',
        end_date: '',
        scope: 'company',
        department: '',
        employee_id: ''
      });
      
      fetchTimesheets();
    } catch (error) {
      console.error('Error generating timesheets:', error);
      toast.error(error.message || 'Failed to generate timesheets');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = () => {
    if (timesheets.length === 0) {
      toast.error('No timesheets to download');
      return;
    }

    // Create CSV content for consolidated timesheets
    const headers = [
      'Employee Name',
      'Department',
      'Period Start',
      'Period End',
      'Total Days',
      'Worked Hours',
      'Regular Hours',
      'Holiday Hours',
      'Unpaid Hours',
      'Overtime Hours',
      'Generated On'
    ];

    const rows = timesheets.map(ts => [
      ts.employee_name,
      ts.department,
      ts.period_start,
      ts.period_end,
      ts.daily_entries?.length || 0,
      ts.total_worked_hours || 0,
      ts.total_regular_hours || 0,
      ts.total_holiday_hours || 0,
      ts.total_unpaid_hours || 0,
      ts.total_overtime_hours || 0,
      new Date(ts.generated_at).toLocaleString('en-GB')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `consolidated_timesheets_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('CSV downloaded successfully');
  };

  const toggleRow = (timesheetId) => {
    setExpandedRows(prev => ({
      ...prev,
      [timesheetId]: !prev[timesheetId]
    }));
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'on time': return 'bg-green-100 text-green-800';
      case 'late': return 'bg-amber-100 text-amber-800';
      case 'incomplete': return 'bg-gray-100 text-gray-800';
      case 'absent': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getFilteredEmployees = () => {
    if (generateForm.scope !== 'individual') return [];
    
    if (generateForm.scope === 'individual' && generateForm.department) {
      return employees.filter(emp => emp.department === generateForm.department);
    }
    
    return employees;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">All Timesheets</h1>
            <p className="text-sm text-gray-600 mt-1">
              Generate and manage timesheets based on clock in/out data
            </p>
          </div>
          <div className="flex gap-3">
            {selectedTimesheets.length > 0 && (
              <Button
                onClick={() => setShowBulkDeleteDialog(true)}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete ({selectedTimesheets.length})
              </Button>
            )}
            <Button
              onClick={() => setShowGenerateDialog(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <FileText className="w-4 h-4 mr-2" />
              Generate Timesheets
            </Button>
            <Button
              onClick={handleDownloadCSV}
              variant="outline"
              disabled={timesheets.length === 0}
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter Timesheets</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label>Start Date</Label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              className="w-full border rounded-md px-3 py-2 mt-1"
            />
          </div>
          <div>
            <Label>End Date</Label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              className="w-full border rounded-md px-3 py-2 mt-1"
            />
          </div>
          <div>
            <Label>Department</Label>
            <select
              value={filters.department}
              onChange={(e) => setFilters({ ...filters, department: e.target.value })}
              className="w-full border rounded-md px-3 py-2 mt-1"
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Employee</Label>
            <select
              value={filters.employee_id}
              onChange={(e) => setFilters({ ...filters, employee_id: e.target.value })}
              className="w-full border rounded-md px-3 py-2 mt-1"
            >
              <option value="">All Employees</option>
              {employees
                .filter(emp => !filters.department || emp.department === filters.department)
                .map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex gap-3">
          <Button onClick={fetchTimesheets} className="bg-teal-600 hover:bg-teal-700">
            Apply Filters
          </Button>
          <Button
            onClick={() => {
              setFilters({ start_date: '', end_date: '', department: '', employee_id: '' });
            }}
            variant="outline"
          >
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Timesheets Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-gray-500">Loading timesheets...</div>
            </div>
          ) : timesheets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <FileText className="w-16 h-16 mb-4 text-gray-300" />
              <p className="text-lg font-medium">No timesheets found</p>
              <p className="text-sm">Generate timesheets to get started</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                    <input
                      type="checkbox"
                      checked={selectedTimesheets.length === timesheets.length && timesheets.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Period
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Worked Hours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Holiday Hours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unpaid Hours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Overtime
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {timesheets.map((ts) => (
                  <>
                    <tr key={ts.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <input
                          type="checkbox"
                          checked={selectedTimesheets.includes(ts.id)}
                          onChange={() => toggleSelectTimesheet(ts.id)}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{ts.employee_name}</div>
                          <div className="text-sm text-gray-500">{ts.department}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(ts.period_start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                          {' - '}
                          {new Date(ts.period_end).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                        <div className="text-xs text-gray-500">
                          {ts.daily_entries?.length || 0} days
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {ts.total_worked_hours ? `${ts.total_worked_hours}h` : '0h'}
                        </div>
                        <div className="text-xs text-gray-500">
                          Regular: {ts.total_regular_hours || 0}h
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-teal-600">
                          {ts.total_holiday_hours ? `${ts.total_holiday_hours}h` : '0h'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-red-600">
                          {ts.total_unpaid_hours ? `${ts.total_unpaid_hours}h` : '0h'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {ts.total_overtime_hours > 0 ? (
                            <span className="text-teal-600 font-medium">+{ts.total_overtime_hours}h</span>
                          ) : (
                            '0h'
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => toggleRow(ts.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {expandedRows[ts.id] ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => {
                            setTimesheetToDelete(ts);
                            setShowDeleteDialog(true);
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                    {expandedRows[ts.id] && (
                      <tr className="bg-gray-50">
                        <td colSpan="9" className="px-6 py-4">
                          <div className="space-y-4">
                            {/* Summary Info */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                              <div>
                                <span className="font-medium text-gray-700">Total Days:</span>
                                <div className="text-gray-900">{ts.daily_entries?.length || 0}</div>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">Generated On:</span>
                                <div className="text-gray-900">
                                  {new Date(ts.generated_at).toLocaleString('en-GB')}
                                </div>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">Generated By:</span>
                                <div className="text-gray-900">{ts.generated_by || 'System'}</div>
                              </div>
                            </div>

                            {/* Daily Breakdown Table */}
                            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                              <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
                                <h4 className="text-sm font-semibold text-gray-700">Daily Breakdown</h4>
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Day</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Clock In/Out</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Hours</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Break</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Notes</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    {ts.daily_entries && ts.daily_entries.length > 0 ? (
                                      ts.daily_entries.map((day, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50">
                                          <td className="px-4 py-2 text-gray-900">
                                            {new Date(day.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                          </td>
                                          <td className="px-4 py-2 text-gray-700">{day.day_of_week}</td>
                                          <td className="px-4 py-2">
                                            {day.clock_in && day.clock_out ? (
                                              <span className="text-gray-900">{day.clock_in} - {day.clock_out}</span>
                                            ) : day.type === 'holiday' ? (
                                              <span className="text-teal-600 font-medium">Holiday</span>
                                            ) : day.type === 'sickness' ? (
                                              <span className="text-orange-600 font-medium">Sickness</span>
                                            ) : day.type === 'absent' ? (
                                              <span className="text-red-600 font-medium">Absent</span>
                                            ) : (
                                              <span className="text-gray-400">-</span>
                                            )}
                                          </td>
                                          <td className="px-4 py-2 text-gray-900 font-medium">
                                            {day.hours ? `${day.hours}h` : '-'}
                                          </td>
                                          <td className="px-4 py-2 text-gray-700">
                                            {day.break_minutes ? `${day.break_minutes}m` : '-'}
                                          </td>
                                          <td className="px-4 py-2">
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                              day.status === 'On Time' ? 'bg-green-100 text-green-800' :
                                              day.status === 'Late' ? 'bg-yellow-100 text-yellow-800' :
                                              day.status === 'Holiday' ? 'bg-teal-100 text-teal-800' :
                                              day.status === 'Sickness' ? 'bg-orange-100 text-orange-800' :
                                              day.status === 'Absent' ? 'bg-red-100 text-red-800' :
                                              day.status === 'Incomplete' ? 'bg-purple-100 text-purple-800' :
                                              'bg-gray-100 text-gray-800'
                                            }`}>
                                              {day.status}
                                            </span>
                                            {day.late_minutes > 0 && (
                                              <div className="text-xs text-red-600 mt-1">
                                                Late by {day.late_minutes}m
                                              </div>
                                            )}
                                          </td>
                                          <td className="px-4 py-2 text-gray-600 text-xs max-w-xs truncate">
                                            {day.notes || '-'}
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="7" className="px-4 py-4 text-center text-gray-500">
                                          No daily entries available
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                  <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                                    <tr className="font-semibold">
                                      <td colSpan="3" className="px-4 py-3 text-right text-gray-700">Totals:</td>
                                      <td className="px-4 py-3 text-gray-900">
                                        Worked: {ts.total_worked_hours || 0}h
                                      </td>
                                      <td className="px-4 py-3 text-teal-600">
                                        Holiday: {ts.total_holiday_hours || 0}h
                                      </td>
                                      <td className="px-4 py-3 text-red-600">
                                        Unpaid: {ts.total_unpaid_hours || 0}h
                                      </td>
                                      <td className="px-4 py-3 text-teal-600">
                                        Overtime: +{ts.total_overtime_hours || 0}h
                                      </td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        {timesheets.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t">
            <p className="text-sm text-gray-600">
              Showing {timesheets.length} timesheet{timesheets.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>

      {/* Generate Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Generate Timesheets</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <input
                  type="date"
                  value={generateForm.start_date}
                  onChange={(e) => setGenerateForm({ ...generateForm, start_date: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 mt-1"
                />
              </div>
              <div>
                <Label>End Date *</Label>
                <input
                  type="date"
                  value={generateForm.end_date}
                  onChange={(e) => setGenerateForm({ ...generateForm, end_date: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 mt-1"
                />
              </div>
            </div>

            {/* Scope Selection */}
            <div>
              <Label>Generate For *</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <button
                  onClick={() => setGenerateForm({ ...generateForm, scope: 'company', department: '', employee_id: '' })}
                  className={`flex flex-col items-center justify-center p-4 border-2 rounded-lg transition-colors ${
                    generateForm.scope === 'company'
                      ? 'border-teal-600 bg-teal-50 text-teal-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Building2 className="w-6 h-6 mb-2" />
                  <span className="text-sm font-medium">Company</span>
                </button>
                <button
                  onClick={() => setGenerateForm({ ...generateForm, scope: 'department', employee_id: '' })}
                  className={`flex flex-col items-center justify-center p-4 border-2 rounded-lg transition-colors ${
                    generateForm.scope === 'department'
                      ? 'border-teal-600 bg-teal-50 text-teal-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Users className="w-6 h-6 mb-2" />
                  <span className="text-sm font-medium">Department</span>
                </button>
                <button
                  onClick={() => setGenerateForm({ ...generateForm, scope: 'individual' })}
                  className={`flex flex-col items-center justify-center p-4 border-2 rounded-lg transition-colors ${
                    generateForm.scope === 'individual'
                      ? 'border-teal-600 bg-teal-50 text-teal-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <User className="w-6 h-6 mb-2" />
                  <span className="text-sm font-medium">Individual</span>
                </button>
              </div>
            </div>

            {/* Department Selection (if scope is department or individual) */}
            {(generateForm.scope === 'department' || generateForm.scope === 'individual') && (
              <div>
                <Label>Department *</Label>
                <select
                  value={generateForm.department}
                  onChange={(e) => setGenerateForm({ ...generateForm, department: e.target.value, employee_id: '' })}
                  className="w-full border rounded-md px-3 py-2 mt-1"
                >
                  <option value="">Select a department</option>
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Employee Selection (if scope is individual) */}
            {generateForm.scope === 'individual' && (
              <div>
                <Label>Employee *</Label>
                <select
                  value={generateForm.employee_id}
                  onChange={(e) => setGenerateForm({ ...generateForm, employee_id: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 mt-1"
                  disabled={!generateForm.department && generateForm.scope === 'individual'}
                >
                  <option value="">
                    {!generateForm.department ? 'Select a department first' : 'Select an employee'}
                  </option>
                  {getFilteredEmployees().map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setShowGenerateDialog(false)} variant="outline">
              Cancel
            </Button>
            <Button
              onClick={handleGenerateTimesheets}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={loading}
            >
              {loading ? 'Generating...' : 'Generate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Timesheet</DialogTitle>
          </DialogHeader>
          
          {timesheetToDelete && (
            <div className="py-4">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete this consolidated timesheet?
              </p>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Employee:</span>
                  <span className="text-sm text-gray-900">{timesheetToDelete.employee_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Period:</span>
                  <span className="text-sm text-gray-900">
                    {new Date(timesheetToDelete.period_start).toLocaleDateString('en-GB')} - {new Date(timesheetToDelete.period_end).toLocaleDateString('en-GB')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Total Days:</span>
                  <span className="text-sm text-gray-900">{timesheetToDelete.daily_entries?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Worked Hours:</span>
                  <span className="text-sm text-gray-900">{timesheetToDelete.total_worked_hours || 0}h</span>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                This action cannot be undone.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowDeleteDialog(false)} variant="outline">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteTimesheet}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Multiple Timesheets</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-gray-700 mb-4">
              Are you sure you want to delete <span className="font-bold">{selectedTimesheets.length}</span> timesheet{selectedTimesheets.length !== 1 ? 's' : ''}?
            </p>
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <div className="flex items-start">
                <Trash2 className="w-5 h-5 text-red-600 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm font-medium text-red-900">Warning</p>
                  <p className="text-sm text-red-700 mt-1">
                    This will permanently delete {selectedTimesheets.length} timesheet{selectedTimesheets.length !== 1 ? 's' : ''} and cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowBulkDeleteDialog(false)} variant="outline">
              Cancel
            </Button>
            <Button
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete {selectedTimesheets.length}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllTimesheets;
